import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAppDispatch } from '../store/hooks';
import { setCurrentOrder } from '../store/slices/orderSlice';
import { menuHeaders, publicMenuApi, publicOrderKey } from '../api/publicMenu';
import { Guest, MenuItem, Order, QrLocation } from '../types';

const OrderPage: React.FC = () => {
  const { locationCode } = useParams<{ locationCode?: string }>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const access = locationCode || params.get('access') || '';
  const [items, setItems] = useState<MenuItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState('');
  const [locationId, setLocationId] = useState<number | null>(null);
  const [locationName, setLocationName] = useState('');
  const [guestAccess, setGuestAccess] = useState('');

  const [guestForm, setGuestForm] = useState({ name: '', phone: '', email: '' });
  const [guestId, setGuestId] = useState<number | null>(null);
  const [cart, setCart] = useState<{ item: MenuItem; qty: number }[]>([]);
  const [step, setStep] = useState<'guest' | 'menu' | 'confirm'>('guest');

  useEffect(() => {
    let active = true;
    setReady(false);
    setItems([]);
    setCart([]);
    setGuestId(null);
    setGuestAccess('');
    setLocationId(null);
    setLocationName('');
    setStep('guest');
    setError('');
    dispatch(setCurrentOrder(null));
    if (!access) {
      setError('Відкрийте чинне посилання меню або QR-код ресторану.');
      return;
    }
    const load = async () => {
      try {
        const headers = menuHeaders(access);
        const [menu, location] = await Promise.all([
          publicMenuApi.get<MenuItem[]>('/menu', { headers }),
          locationCode ? publicMenuApi.get<QrLocation>(`/qr/scan/${encodeURIComponent(locationCode)}`) : Promise.resolve(null),
        ]);
        if (!active) return;
        if (menu.data.length === 0) {
          setError('За цим посиланням немає доступних позицій QR-замовлення.');
          return;
        }
        setItems(menu.data);
        setLocationId(location?.data.id ?? null);
        setLocationName(location?.data.name ?? '');
        setReady(true);
      } catch {
        if (active) setError('Публікація або QR-код недоступні, відкликані чи прострочені.');
      }
    };
    void load();
    return () => { active = false; };
  }, [access, dispatch, locationCode]);

  const handleGuestSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ready || loading) return;
    setLoading(true);
    setError('');
    try {
      const { data } = await publicMenuApi.post<{ guest: Guest; accessToken: string }>('/guests/public', {
        ...guestForm,
        discountType: 'None',
        discountValue: 0,
        isDiscountActive: false,
      }, { headers: menuHeaders(access) });
      setGuestId(data.guest.id);
      setGuestAccess(data.accessToken);
      setStep('menu');
    } catch {
      setError('Не вдалося зареєструвати гостя. Перевірте чинність посилання ресторану.');
    } finally {
      setLoading(false);
    }
  };

  const addToCart = (item: MenuItem) => {
    setCart((prev) => {
      const existing = prev.find((c) => c.item.id === item.id);
      if (existing) return prev.map((c) => (c.item.id === item.id ? { ...c, qty: c.qty + 1 } : c));
      return [...prev, { item, qty: 1 }];
    });
  };

  const removeFromCart = (itemId: number) => {
    setCart((prev) => prev.filter((c) => c.item.id !== itemId));
  };

  const total = cart.reduce((sum, c) => sum + c.item.price * c.qty, 0);

  const handleOrder = async () => {
    if (!guestId || cart.length === 0 || loading) return;
    setLoading(true);
    setError('');
    try {
      const { data } = await publicMenuApi.post<Order>('/orders', {
        guestId,
        qrLocationId: locationId,
        items: cart.map((c) => ({ menuItemId: c.item.id, quantity: c.qty })),
      }, { headers: menuHeaders(access, guestAccess) });
      sessionStorage.setItem(publicOrderKey(data.id), JSON.stringify({ access, guestAccess }));
      dispatch(setCurrentOrder(data));
      navigate(`/payment/${data.id}`);
    } catch {
      setError('Замовлення не підтверджено. Перевірте чинність меню та доступність позицій перед повторною спробою.');
    } finally {
      setLoading(false);
    }
  };

  if (step === 'guest') {
    return (
      <div style={{ padding: '24px', maxWidth: '500px', margin: '40px auto' }}>
        <h2 style={{ color: 'var(--crm-text)', textAlign: 'center' }}>Ласкаво просимо!</h2>
        {locationName && <p>Зона: {locationName}</p>}
        {error && <p role="alert">{error}</p>}
        <div
          style={{
            background: 'var(--crm-surface)',
            color: 'var(--crm-text)',
            border: '1px solid var(--crm-border)',
            padding: '24px',
            borderRadius: 'var(--crm-radius-md)',
            boxShadow: 'var(--crm-shadow-sm)',
          }}
        >
          <h3 style={{ marginTop: 0, color: 'var(--crm-text)' }}>Ваші дані</h3>
          <form onSubmit={handleGuestSubmit}>
            {[
              ["Ім'я", 'name', 'text'],
              ['Телефон', 'phone', 'tel'],
              ['Email', 'email', 'email'],
            ].map(([label, key, type]) => (
              <div key={key} style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', marginBottom: '4px', color: 'var(--crm-text)' }}>{label}</label>
                <input
                  type={type}
                  value={guestForm[key as keyof typeof guestForm]}
                  onChange={(e) => setGuestForm({ ...guestForm, [key]: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid var(--crm-border)',
                    borderRadius: 'var(--crm-radius-sm)',
                    background: 'var(--crm-input-bg)',
                    color: 'var(--crm-text)',
                    boxSizing: 'border-box',
                  }}
                  required
                />
              </div>
            ))}
            <button
              type="submit"
              disabled={!ready || loading}
              style={{
                width: '100%',
                padding: '12px',
                background: 'var(--crm-accent)',
                color: 'var(--crm-accent-contrast)',
                borderRadius: 'var(--crm-radius-sm)',
                cursor: 'pointer',
                fontSize: '16px',
                marginTop: '8px',
              }}
            >
              Перейти до меню
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (step === 'menu') {
    return (
      <div style={{ padding: '24px', maxWidth: '1000px', margin: '0 auto' }}>
        <h2 style={{ color: 'var(--crm-text)' }}>Оберіть страви</h2>
        {error && <p role="alert">{error}</p>}
        <div className="qr-order-layout">
          <div>
            {Array.from(new Set(items.map((i) => i.category))).map((cat) => (
              <div key={cat} style={{ marginBottom: '20px' }}>
                <h3 style={{ color: 'var(--crm-text)', background: 'var(--crm-surface-alt)', border: '1px solid var(--crm-border)', padding: '8px 16px', borderRadius: 'var(--crm-radius-sm)' }}>
                  {cat}
                </h3>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                    gap: '12px',
                  }}
                >
                  {items
                    .filter((i) => i.category === cat)
                    .map((item) => (
                      <div
                        key={item.id}
                        style={{
                          background: 'var(--crm-surface)',
                          color: 'var(--crm-text)',
                          border: '1px solid var(--crm-border)',
                          borderRadius: 'var(--crm-radius-md)',
                          boxShadow: 'var(--crm-shadow-sm)',
                          padding: '12px',
                          cursor: 'pointer',
                        }}
                        onClick={() => addToCart(item)}
                      >
                        <h4 style={{ margin: '0 0 4px', color: 'var(--crm-text)', fontSize: '14px' }}>{item.name}</h4>
                        <p style={{ margin: '0 0 8px', color: 'var(--crm-text-muted)', fontSize: '12px' }}>{item.description}</p>
                        <p style={{ margin: 0, color: 'var(--crm-accent)', fontWeight: 'bold' }}>{item.price.toFixed(2)} ₴</p>
                      </div>
                    ))}
                </div>
              </div>
            ))}
          </div>
          <div className="qr-order-cart">
            <div
              style={{
                background: 'var(--crm-surface)',
                color: 'var(--crm-text)',
                border: '1px solid var(--crm-border)',
                borderRadius: 'var(--crm-radius-md)',
                boxShadow: 'var(--crm-shadow-sm)',
                padding: '20px',
              }}
            >
              <h3 style={{ marginTop: 0, color: 'var(--crm-text)' }}>Кошик</h3>
              {cart.length === 0 ? (
                <p style={{ color: 'var(--crm-text-muted)' }}>Порожній</p>
              ) : (
                <>
                  {cart.map((c) => (
                    <div
                      key={c.item.id}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '8px 0',
                        borderBottom: '1px solid var(--crm-table-row-border)',
                      }}
                    >
                      <span style={{ fontSize: '14px' }}>
                        {c.item.name} x{c.qty}
                      </span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ color: 'var(--crm-accent)', fontWeight: 'bold' }}>
                          {(c.item.price * c.qty).toFixed(2)} ₴
                        </span>
                        <button
                          onClick={() => removeFromCart(c.item.id)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: 'var(--crm-danger)',
                            cursor: 'pointer',
                            fontSize: '16px',
                          }}
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  ))}
                  <div style={{ fontWeight: 'bold', fontSize: '18px', margin: '12px 0', color: 'var(--crm-text)' }}>
                    Всього: {total.toFixed(2)} ₴
                  </div>
                  <button
                    onClick={handleOrder}
                    disabled={loading}
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: 'var(--crm-success-bg)',
                      color: 'var(--crm-success)',
                      border: '1px solid var(--crm-success)',
                      borderRadius: 'var(--crm-radius-sm)',
                      cursor: 'pointer',
                      fontSize: '16px',
                    }}
                  >
                    {loading ? 'Оформлення...' : 'Замовити'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default OrderPage;
